import React, { useMemo } from "react"
import {
  Table,
  Switch,
  Typography,
  MailOutlined,
  MessageOutlined,
} from "@mimber/ui-web"
import type { ColumnsType } from "@mimber/ui-web"
import { NOTIFICATION_EVENT_CONFIG, CommunicationChannel } from "@mimber/schema"
import type {
  NotificationPreferenceMatrixProps,
  NotificationMatrixRow,
} from "./NotificationPreferenceMatrix.types"
import { buildPendingKey } from "./NotificationPreferenceMatrix.types"
import {
  CHANNEL_LABELS,
  CHANNEL_ORDER,
} from "./NotificationPreferenceMatrix.utils"

const { Text } = Typography

const CHANNEL_ICONS: Partial<Record<CommunicationChannel, React.ReactNode>> = {
  [CommunicationChannel.EMAIL]: <MailOutlined />,
  [CommunicationChannel.SMS]: <MessageOutlined />,
}

/**
 * NotificationPreferenceMatrix — an editable matrix of notification event
 * types (rows) x communication channels (columns), rendered as switches.
 * Presentational only: data and toggle handling are owned by the caller.
 */
export const NotificationPreferenceMatrix: React.FunctionComponent<
  NotificationPreferenceMatrixProps
> = ({ title, preferences, onToggle, pendingKey }) => {
  const rows: NotificationMatrixRow[] = useMemo(() => {
    const rowsByEventType = new Map<string, NotificationMatrixRow>()

    for (const preference of preferences) {
      const { eventType, channel, enabled, locked } = preference
      let row = rowsByEventType.get(eventType)
      if (!row) {
        const config = NOTIFICATION_EVENT_CONFIG[eventType]
        row = {
          key: eventType,
          eventType,
          label: config.label,
          description: config.description,
          channels: {},
        }
        rowsByEventType.set(eventType, row)
      }
      row.channels[channel] = { enabled, locked }
    }

    return Array.from(rowsByEventType.values())
  }, [preferences])

  const channels = useMemo(() => {
    const present = new Set<CommunicationChannel>()
    for (const preference of preferences) {
      present.add(preference.channel)
    }
    return CHANNEL_ORDER.filter((channel) => present.has(channel))
  }, [preferences])

  const columns: ColumnsType<NotificationMatrixRow> = useMemo(
    () => [
      {
        title: "Notification",
        dataIndex: "label",
        key: "label",
        render: (_: string, row) => (
          <div>
            <Text strong>{row.label}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.description}
            </Text>
          </div>
        ),
      },
      ...channels.map((channel) => ({
        title: (
          <>
            {CHANNEL_ICONS[channel]} {CHANNEL_LABELS[channel]}
          </>
        ),
        dataIndex: channel,
        key: channel,
        align: "center" as const,
        width: 100,
        render: (_: unknown, row: NotificationMatrixRow) => {
          const cell = row.channels[channel]
          if (!cell) return null

          const key = buildPendingKey(row.eventType, channel)
          const isPending = pendingKey === key
          return (
            <Switch
              checked={cell.enabled}
              disabled={cell.locked || isPending}
              loading={isPending}
              onChange={(checked) => onToggle(row.eventType, channel, checked)}
            />
          )
        },
      })),
    ],
    [channels, pendingKey, onToggle]
  )

  if (rows.length === 0) return null

  return (
    <div style={{ marginBottom: 24 }}>
      <Text strong style={{ fontSize: 16, display: "block", marginBottom: 12 }}>
        {title}
      </Text>
      <Table<NotificationMatrixRow>
        bordered
        columns={columns}
        dataSource={rows}
        pagination={false}
        rowKey="key"
      />
    </div>
  )
}
